import type { Rect } from '../../../engine/hex/types';
import type { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme, TextVariant } from '../ModalTheme';
import type { HitTarget, MeasureResult, UiItem } from './Types';
import { wrapTextLines } from './Utils';

export class TextItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };

    constructor(
        private text: string,
        private variant: TextVariant,
        private options: {
            align?: CanvasTextAlign;
            color?: string | ((theme: ModalTheme) => string);
        } = {},
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        ctx.font = theme.fonts[this.variant];
        const width = Math.min(maxW, ctx.measureText(this.text).width);
        return { w: width, h: theme.lineHeights[this.variant] };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        const h = theme.lineHeights[this.variant];
        this.rect = { x, y, w, h };
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.font = theme.fonts[this.variant];
        ctx.textBaseline = 'top';

        let color = theme.colors.bodyText;
        if (this.variant === 'muted') color = theme.colors.mutedText;
        if (this.variant === 'section') color = theme.colors.sectionText;
        if (this.variant === 'code') color = theme.colors.codeText;
        if (this.variant === 'header') color = theme.colors.headerText;
        if (this.options.color) {
            color =
                typeof this.options.color === 'function'
                    ? this.options.color(theme)
                    : this.options.color;
        }

        ctx.fillStyle = color;

        const prevAlign = ctx.textAlign;
        const align = this.options.align ?? 'left';
        ctx.textAlign = align;

        const x =
            align === 'left'
                ? this.rect.x
                : align === 'center'
                  ? this.rect.x + this.rect.w / 2
                  : this.rect.x + this.rect.w;

        ctx.fillText(this.text, x, this.rect.y);

        ctx.textAlign = prevAlign;
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}

export type WrappedTextOptions = {
    align?: CanvasTextAlign;
    color?: string | ((theme: ModalTheme) => string);
};

export class WrappedTextItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private lines: string[] = [];

    constructor(
        private text: string,
        private variant: TextVariant,
        private minLines: number = 0,
        private options: WrappedTextOptions = {},
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        ctx.font = theme.fonts[this.variant];
        this.lines = wrapTextLines(ctx, this.text, maxW);
        while (this.lines.length < this.minLines) this.lines.push('');

        let maxLineW = 0;
        this.lines.forEach((line) => {
            maxLineW = Math.max(maxLineW, ctx.measureText(line).width);
        });

        return {
            w: Math.min(maxW, maxLineW),
            h: this.lines.length * theme.lineHeights[this.variant],
        };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        this.rect = {
            x,
            y,
            w,
            h: this.lines.length * theme.lineHeights[this.variant],
        };
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.font = theme.fonts[this.variant];
        ctx.textBaseline = 'top';
        let color = theme.colors.bodyText;
        if (this.variant === 'muted') color = theme.colors.mutedText;
        if (this.variant === 'section') color = theme.colors.sectionText;
        if (this.variant === 'code') color = theme.colors.codeText;
        if (this.variant === 'header') color = theme.colors.headerText;
        if (this.options.color) {
            color =
                typeof this.options.color === 'function'
                    ? this.options.color(theme)
                    : this.options.color;
        }
        ctx.fillStyle = color;

        const prevAlign = ctx.textAlign;
        const align = this.options.align ?? 'left';
        ctx.textAlign = align;

        const x =
            align === 'left'
                ? this.rect.x
                : align === 'center'
                  ? this.rect.x + this.rect.w / 2
                  : this.rect.x + this.rect.w;

        this.lines.forEach((line, index) => {
            ctx.fillText(line, x, this.rect.y + index * theme.lineHeights[this.variant]);
        });

        ctx.textAlign = prevAlign;
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}
